import { useCallback, useEffect, useState } from "react";
import {
  elevateOwnerSession,
  fetchSecuritySummary,
  generateOwnerRecoveryKey,
  revokeAllSessionsSecure,
  type SecuritySummary,
} from "../api/accountSecurityApi";
import { useAuth } from "../state/AuthProvider";

type OwnerTotpModalProps = {
  open: boolean;
  title: string;
  purpose:
    | "owner_recovery_key_generate"
    | "owner_recovery_key_rotate"
    | "revoke_all_sessions";
  onClose(): void;
  onVerified(): void;
};

export function OwnerTotpModal({ open, title, purpose, onClose, onVerified }: OwnerTotpModalProps) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setCode("");
      setError(null);
    }
  }, [open]);

  const submit = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await elevateOwnerSession(code.trim(), purpose);
      onVerified();
      onClose();
    } catch {
      setError("Invalid code or verification failed.");
    } finally {
      setBusy(false);
    }
  }, [code, onClose, onVerified, purpose]);

  if (!open) return null;

  return (
    <div className="owner-totp-modal" role="dialog" aria-modal="true" aria-labelledby="owner-totp-title">
      <div className="owner-totp-modal__backdrop" onClick={onClose} aria-hidden="true" />
      <div className="owner-totp-modal__panel">
        <h2 id="owner-totp-title">{title}</h2>
        <p className="muted">Enter your current authenticator code to verify it&apos;s you.</p>
        <label className="field-label auth-field">
          6-digit code
          <input
            className="field-input auth-input auth-totp-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
          />
        </label>
        {error ? (
          <p className="auth-error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="owner-totp-modal__actions">
          <button type="button" className="text-button" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="primary-button" disabled={busy || code.length < 6} onClick={() => void submit()}>
            Verify
          </button>
        </div>
      </div>
    </div>
  );
}

export function AccountSecurityPage() {
  const { user, logout } = useAuth();
  const [summary, setSummary] = useState<SecuritySummary | null>(null);
  const [totpPurpose, setTotpPurpose] = useState<
    "owner_recovery_key_generate" | "owner_recovery_key_rotate" | "revoke_all_sessions" | null
  >(null);
  const [ownerKeyPlain, setOwnerKeyPlain] = useState<string | null>(null);
  const [ownerKeyAck, setOwnerKeyAck] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    void fetchSecuritySummary()
      .then(setSummary)
      .catch(() => setSummary(null));
  }, []);

  if (!user) return null;

  const isOwner = user.isAdmin === true;

  async function afterTotpVerified() {
    if (totpPurpose === "revoke_all_sessions") {
      await revokeAllSessionsSecure();
      await logout();
      window.location.href = "/login";
      return;
    }
    if (
      totpPurpose === "owner_recovery_key_generate" ||
      totpPurpose === "owner_recovery_key_rotate"
    ) {
      const result = await generateOwnerRecoveryKey();
      setOwnerKeyPlain(result.recoveryKey);
      setOwnerKeyAck(false);
      setSummary((prev) => (prev ? { ...prev, ownerRecoveryKeyConfigured: true } : prev));
      setMessage("Save your Owner Recovery Key offline. It will not be shown again.");
    }
  }

  return (
    <article className="account-page">
      <h1>Security</h1>
      <p className="account-page__lead">Protect your FSWL account.</p>

      <ul className="account-security-list">
        <li>
          <strong>Authenticator</strong>
          <span className="muted">
            {summary?.totpEnrolled ? "Enabled" : "Not enrolled — complete setup at sign-in."}
          </span>
        </li>
        <li>
          <strong>Active sessions</strong>
          {isOwner ? (
            <button
              type="button"
              className="text-button"
              onClick={() => setTotpPurpose("revoke_all_sessions")}
            >
              Revoke all sessions (requires TOTP)
            </button>
          ) : (
            <span className="muted">Use Sign Out from the account menu on shared devices.</span>
          )}
        </li>
      </ul>

      {isOwner ? (
        <section className="account-owner-panel">
          <h2>System Owner</h2>
          <p className="account-page__lead">Owner Protection: Enabled</p>
          <dl className="account-details">
            <div>
              <dt>Owner Recovery Key</dt>
              <dd>{summary?.ownerRecoveryKeyConfigured ? "Configured" : "Not configured"}</dd>
            </div>
          </dl>
          {!ownerKeyPlain ? (
            <button
              type="button"
              className="primary-button auth-submit"
              onClick={() =>
                setTotpPurpose(
                  summary?.ownerRecoveryKeyConfigured
                    ? "owner_recovery_key_rotate"
                    : "owner_recovery_key_generate",
                )
              }
            >
              {summary?.ownerRecoveryKeyConfigured
                ? "Rotate Owner Recovery Key"
                : "Generate Owner Recovery Key"}
            </button>
          ) : (
            <div className="account-owner-key-reveal">
              <p className="auth-error" role="status">
                Copy this Owner Recovery Key now. It will not be shown again.
              </p>
              <code className="account-owner-key-value">{ownerKeyPlain}</code>
              <label className="auth-recovery-ack">
                <input
                  type="checkbox"
                  checked={ownerKeyAck}
                  onChange={(event) => setOwnerKeyAck(event.target.checked)}
                />
                I have saved my Owner Recovery Key
              </label>
              <button
                type="button"
                className="primary-button auth-submit"
                disabled={!ownerKeyAck}
                onClick={() => {
                  setOwnerKeyPlain(null);
                  setMessage(null);
                }}
              >
                Done
              </button>
            </div>
          )}
        </section>
      ) : null}

      {message ? <p className="muted">{message}</p> : null}

      <OwnerTotpModal
        open={totpPurpose !== null && !ownerKeyPlain}
        title="Verify it’s you"
        purpose={totpPurpose ?? "owner_recovery_key_generate"}
        onClose={() => setTotpPurpose(null)}
        onVerified={() => void afterTotpVerified()}
      />
    </article>
  );
}
